<template>
  <div class="ai-agent-container" :class="{ 'light-theme': isLightTheme }">
    <!-- AI Button -->
    <button 
      class="ai-button" 
      :class="{ 'is-active': isOpen }"
      @click="toggleChat"
    >
      <img 
        :src="prepareAvatarUrl(currentAgent.avatar)"
        alt="AI Assistant" 
        class="ai-button-image"
      />
      <div class="ai-button-glow"></div>
      
      <!-- First Visit Tooltip -->
      <Transition name="tooltip">
        <div v-if="showFirstVisitTooltip" class="first-visit-tooltip">
          <p>Hello! I am your AI Agent. Let's talk</p>
          <button class="tooltip-close" @click.stop="closeTooltip">×</button>
        </div>
      </Transition>
    </button>

    <!-- Chat Window -->
    <Transition name="chat-window">
      <div v-if="isOpen" class="chat-window" :class="{ 'mobile': isMobile }">
        <div class="chat-header">
          <div class="header-left">
            <Transition name="tooltip">
              <div v-if="showAgentTooltip" class="agent-tooltip">
                <p>Your AI chats here. Switch between</p>
                <button class="tooltip-close" @click.stop="closeAgentTooltip">×</button>
              </div>
            </Transition>
            <button class="avatar-button pulse-animation" type="button" @click.prevent.stop="toggleAgentsList">
              <img :src="prepareAvatarUrl(currentAgent.avatar)" alt="AI Agent" class="header-avatar" />
            </button>
            <div class="header-title">
              <h3 class="chat-header-title">{{ currentAgent.name }}</h3>
              <button @click="openAgentProfile(currentAgent.agentTag)" class="agent-tag-button">{{ currentAgent.agentTag }}</button>
            </div>
          </div>
          <div class="header-controls">
            <button class="theme-toggle" @click="toggleTheme">
              <i class="fas" :class="isLightTheme ? 'fa-moon' : 'fa-sun'"></i>
            </button>
            <button class="close-button" @click="toggleChat">×</button>
          </div>
        </div>
        <div class="chat-messages">
          <div v-if="currentAgent.agentTag === 'main_agent'" class="message agent-message">
            <div class="message-content">
              <div class="typing-animation">
                <p v-if="currentAgent.agentTag === 'main_agent'">Hello! How are you today?👋 I am your BITCAT AI Agent. Let me introduce you to our Universe! 🚀</p>
                <div class="tech-decoration">
                  <div class="tech-line"></div>
                  <div class="tech-dot"></div>
                </div>
              </div>
            </div>
          </div>
          
          <div v-if="currentPageInfo && currentAgent.agentTag === 'main_agent'" class="message agent-message">
            <div class="message-content">
              <div class="typing-animation">
                <p>About page <small>(are you here)</small><br>{{ currentPageInfo.title }}</p>
                <p @click="handleClick" v-html="currentPageInfo.description"></p>
                <p>Let me know if you have any questions</p>
                <div class="tech-decoration">
                  <div class="tech-line"></div>
                  <div class="tech-dot"></div>
                </div>
              </div>
            </div>
          </div>

          <!-- Add more messages here form agent and user -->
          <div v-for="(msg, index) in currentAgentHistory" :key="index" 
               :class="['message', msg.type === 'user' ? 'user-message' : 'agent-message']">
            <div class="message-content">
              <div :class="{ 'typing-animation': msg.type === 'agent' }">
                <p>{{ msg.content }}</p>
                <div v-if="msg.type === 'agent'" class="tech-decoration">
                  <div class="tech-line"></div>
                  <div class="tech-dot"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="chat-input">
          <div class="input-wrapper">
            <input 
              type="text" 
              v-model="message" 
              @keyup.enter="!isSending && sendMessage()"
              placeholder="Message..."
            />
          </div>
          <button @click="sendMessage" class="send-button" :disabled="isSending">
            <i class="fas" :class="isSending ? 'fa-spinner fa-spin' : 'fa-paper-plane'"></i>
          </button>
        </div>

        <Transition name="chat-window">
          <AiAgentsList 
            v-if="isOpen"
            :show="showAgentsList"
            :is-light-theme="isLightTheme"
            @close="store.commit('TOGGLE_AGENTS_LIST', false)"
            @select-agent="selectAgent"
          />
        </Transition>
      </div>
      
    </Transition>
    
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch, computed, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import AiAgentsList from './AiAgentsList.vue'

const store = useStore()
const route = useRoute()
const router = useRouter()

const isOpen = computed(() => store.getters.chatOpen)
const message = ref('')
const isMobile = ref(false)
const isLightTheme = ref(false)
const showAgentsList = computed({
  get: () => store.getters.agentsListOpen,
  set: (value) => store.commit('TOGGLE_AGENTS_LIST', value)
})

const currentAgent = computed(() => store.getters.currentAgent)
const showFirstVisitTooltip = ref(false)
const showAgentTooltip = ref(false)

const isSending = ref(false)
const chatHistory = ref({})
const currentAgentHistory = computed(() => {
  const history = chatHistory.value[currentAgent.value.agentTag] || []
  return history.slice(-20)
})

const toggleChat = () => {
  store.commit('TOGGLE_CHAT', !isOpen.value)
}

const prepareAvatarUrl = (avatarNum) => {
  return `https://solanabitcat.com/data/ai-agents/bitcat-ai-agent-avatar-${avatarNum}.png`
}

const openAgentProfile = (agentTag) => {
  toggleChat()
  router.push(`/ai-agents/${agentTag}`)
}

const updatePageInfo = () => {
  const routeName = route.name
  if (pagesInfo[routeName]) {
    currentPageInfo.value = {
      ...pagesInfo[routeName],
      routeName
    }
  } else {
    pagesInfo.NoDescription.title = routeName
    currentPageInfo.value = {
      ...pagesInfo.NoDescription,
      routeName
    }
  }
}

const handleClick = (event) => {
  const path = event.target.dataset.path;
  if(!path) return
  goTo(path);
}

const goTo = (path) => {
  isOpen.value = false

  setTimeout(() => {
    router.push(path)
  }, 250)
}

const pagesInfo = {
  Universe: {
    title: "BITCAT Universe! 🌌",
    description: "This is your gateway to the exciting world of BITCAT. Here you can explore various game modes with <button class='button-link' data-path='/solo'>Solo Game</button>, <button class='button-link' data-path='/hide-seek'>PvP Game</button>, Multiplayer Game(soon) and <button class='button-link' data-path='/leaderboard'>Leaderboard</button>. Game Market with <button class='button-link' data-path='/nft-market'>NFT Marketplace</button>, Assets Exchange(soon). BITCAT Society with <button class='button-link' data-path='/dao'>DAO Voting</button>, MEME Gallery(soon). Check it out and start your adventure! Find out more by clicking on the About BITCAT Universe button."
  },
  SoloGame: {
    title: "Solo Game 🎮",
    description: "Challenge yourself in this single-player mode! PUMP cat and to get points(Weight). You need it for <button class='button-link' data-path='/hide-seek'>PvP Game</button> battle. Can you make it to the top? 🏆"
  },
  Leaderboard: {
    title: "Leaderboard 🏆",
    description: "Check out the top players by Best score, Cat weight and Multiplier win/lose. Can you make it to the top?"
  },
  Roadmap: {
    title: "BITCAT Roadmap 🗺️",
    description: "Explore our exciting plans for the future! See what features and updates are coming next."
  },
  MyBITCAT: {
    title: "My BITCAT Profile 😺",
    description: "Your personal space where you can manage your BITCAT, check stats, and view your achievements."
  },
  MultiplayerGame: {
    title: "Multiplayer Arena 🤺",
    description: "Challenge other players in real-time battles! Show your skills and climb the ranks."
  },
  'nft-market': {
    title: "NFT Marketplace 🏪",
    description: "Browse, buy, and sell unique BITCAT NFTs. Find rare items and special collections! Coming soon."
  },
  Player: {
    title: "Player Profile 👤",
    description: "View detailed information about players, their achievements, rates, and game history."
  },
  Arena: {
    title: "Battle Arena ⚔️",
    description: "Enter the arena and face off against other players in epic battles!"
  },
  HideSeek: {
    title: "Hide & Seek Game(PvP) 🕵️",
    description: "A thrilling game mode where stealth and luck are key to victory. No more points to play? Let's PUMP your cat on <button class='button-link' data-path='/solo'>Solo Game</button> to get points 🏆"
  },
  Dao: {
    title: "DAO Voting 🏛️",
    description: "Participate in the BITCAT community governance. Vote on proposals and shape the future of the BITCAT Universe! Create your own proposals and share your ideas with the community. It's all for free. You just need to have BITCAT tokents on your wallet to vote."
  },
  Changelog: {
    title: "Changelog 📝",
    description: "Stay up to date with the latest updates and improvements to BITCAT. Here we share our progress and features log."
  },
  NoDescription: {
    title: "",
    description: "I think this page is clear without my explanation. 🤓"
  }
}

const currentPageInfo = ref(null)

const toggleAgentsList = () => {
  store.commit('TOGGLE_AGENTS_LIST')
}

const selectAgent = (agent) => {
  store.commit('SELECT_AGENT', agent)
}

const addMessageToHistory = (content, type, messageId) => {
  const agentTag = currentAgent.value.agentTag
  if (!chatHistory.value[agentTag]) {
    chatHistory.value[agentTag] = []
  }
  
  if (messageId) {
    // Update existing message
    const messageIndex = chatHistory.value[agentTag].findIndex(msg => msg.id === messageId)
    if (messageIndex !== -1) {
      if(chatHistory.value[agentTag][messageIndex].content === '...') {
        chatHistory.value[agentTag][messageIndex].content = ''
      }

      if (!chatHistory.value[agentTag][messageIndex].content.endsWith(content)) {
          chatHistory.value[agentTag][messageIndex].content += content;
      }
    }
  } else {
    // Add new message
    messageId = Math.random().toString(36).substr(2, 9)
    chatHistory.value[agentTag].push({
      id: messageId,
      content,
      type,
      timestamp: Date.now()
    })
  }

  // Keep only last 20 messages
  if (chatHistory.value[agentTag].length > 20) {
    chatHistory.value[agentTag] = chatHistory.value[agentTag].slice(-20)
  }

  // Save to localStorage
  localStorage.setItem('chatHistory', JSON.stringify(chatHistory.value))
  scrollToBottom()

  return messageId
}

const scrollToBottom = () => {
  nextTick(() => {
    const chatMessages = document.querySelector('.chat-messages')
    if (chatMessages) {
      chatMessages.scrollTop = chatMessages.scrollHeight
    }
  })
}

const sendMessage = async () => {
  if (!message.value.trim() || isSending.value) return

  isSending.value = true
  const userMessage = message.value
  message.value = ''

  // Add user message
  addMessageToHistory(userMessage, 'user')
  
  // Add temporary agent message
  const messageId = addMessageToHistory('...', 'agent')
  
  try {
    store.getters.socket.emit('user-reply', {
      messageId,
      message: userMessage,
      agentTag: currentAgent.value.agentTag
    })
  } catch (error) {
    console.error('Error sending message:', error)
    isSending.value = false
  }
}

const initializeSocket = () => {
  store.getters.socket.on('ai-reply', (data) => {
    const { message, agentTag, messageId } = data
    
    if (agentTag === currentAgent.value.agentTag) {
      addMessageToHistory(message, 'agent', messageId)
      // Remove last agent message and add new one
      // const agentMessages = chatHistory.value[agentTag].filter(msg => msg.type === 'agent')
      // if (agentMessages.length > 0) {
      //   const lastIndex = chatHistory.value[agentTag].indexOf(agentMessages[agentMessages.length - 1])
      //   if (lastIndex !== -1) {
      //     chatHistory.value[agentTag][lastIndex].content = message
      //   }
      // }
    }
    
    isSending.value = false
    // scrollToBottom()
  })

  store.getters.socket.on('ai-error', (data) => {
    const { message, agentTag, messageId } = data
    if (agentTag === currentAgent.value.agentTag) {
      addMessageToHistory(message, 'agent', messageId)
      // Remove last agent message and add error message
      // const agentMessages = chatHistory.value[agentTag].filter(msg => msg.type === 'agent')
      // if (agentMessages.length > 0) {
      //   const lastIndex = chatHistory.value[agentTag].indexOf(agentMessages[agentMessages.length - 1])
      //   if (lastIndex !== -1) {
      //     chatHistory.value[agentTag][lastIndex].content = `Error: ${message}`
      //   }
      // }
    }
    isSending.value = false
    // scrollToBottom()
  })
}

const initSocket = async () => {
  await store.dispatch('initSocket')
}

const toggleTheme = () => {
  isLightTheme.value = !isLightTheme.value
}

const checkMobile = () => {
  isMobile.value = window.innerWidth <= 768
}

const checkFirstVisit = () => {
  const hasVisited = localStorage.getItem('bitcat_ai_visited')
  if (!hasVisited) {
    showFirstVisitTooltip.value = true
  }
}

const closeTooltip = () => {
  showFirstVisitTooltip.value = false
  localStorage.setItem('bitcat_ai_visited', 'true')
}

const closeAgentTooltip = () => {
  showAgentTooltip.value = false
  localStorage.setItem('agentTooltipShown', 'true')
}

const checkFirstTimeAgent = () => {
  const tooltipShown = localStorage.getItem('agentTooltipShown')
  if (!tooltipShown) {
    showAgentTooltip.value = true
  }
}

onMounted(async () => {
  const savedHistory = localStorage.getItem('chatHistory')
  if (savedHistory) {
    chatHistory.value = JSON.parse(savedHistory)
  }
  scrollToBottom()

  checkMobile()
  window.addEventListener('resize', checkMobile)
  checkFirstVisit()
  checkFirstTimeAgent()
})

onUnmounted(() => {
  store.dispatch('cleanup')
})

const socketConnected = computed(() => store.getters.isSocketConnected)
watch(socketConnected, (newValue) => {
  if (newValue) {
    initializeSocket()
  }
})

watch(() => currentAgent.value, () => {
  scrollToBottom()
})

watch(() => isOpen.value, async () => {
  if (isOpen.value) {
    if(!store.getters.isSocketConnected) {
      await initSocket()
    }
  } else {
    // store.dispatch('cleanup')
  }
  scrollToBottom()
})

watch(() => store.getters.currentAgent, (newAgent) => {
  if (newAgent && newAgent.agentTag) {
    // Load chat history for this agent
    const savedHistory = localStorage.getItem('chatHistory')
    if (savedHistory) {
      const parsedHistory = JSON.parse(savedHistory)
      chatHistory.value = parsedHistory
      currentAgentHistory.value = parsedHistory[newAgent.agentTag] || []
    }
    
    // Store agent info if not already stored
    const agentInfo = JSON.parse(localStorage.getItem('agentInfo') || '{}')
    if (!agentInfo[newAgent.agentTag]) {
      agentInfo[newAgent.agentTag] = {
        name: newAgent.name,
        agentTag: newAgent.agentTag,
        avatar: newAgent.avatar
      }
      localStorage.setItem('agentInfo', JSON.stringify(agentInfo))
    }
  }
}, { immediate: true })

watch(() => route.name, (newRoute) => {
  currentPageInfo.value = null
  if (isOpen.value) {
    setTimeout(() => {
      updatePageInfo()
    }, 250)
  }
})

watch(isOpen, (newValue) => {
  if (newValue) {
    updatePageInfo()
  }
})
</script>

<style scoped>
.ai-agent-container {
  position: fixed;
  right: 20px;
  bottom: 20px;
  z-index: 1000;
}

.ai-button {
  position: relative;
  width: 70px;
  height: 70px;
  border-radius: 50%;
  border: none;
  background: transparent;
  cursor: pointer;
  transition: transform 0.3s ease;
  overflow: visible;
  animation: float 6s ease-in-out infinite;
  z-index: 1000;
}

.ai-button:hover {
  transform: scale(1.05);
}

@keyframes float {
  0% {
    transform: translateY(0) scale(1);
  }
  50% {
    transform: translateY(-7px) scale(1.04);
  }
  100% {
    transform: translateY(0) scale(1);
  }
}

.ai-button-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
}

.ai-button-glow {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: radial-gradient(circle, rgba(82, 255, 168, 0.3) 0%, rgba(0,0,0,0) 70%);
  opacity: 0;
  transition: opacity 0.3s ease;
  border-radius: 50%;
}

.ai-button:hover .ai-button-glow {
  opacity: 1;
  animation: pulse 2s infinite;
}

.chat-window {
  position: fixed;
  right: 20px;
  bottom: 100px;
  width: 380px;
  height: 600px;
  background: var(--window-bg);
  border: 2px solid var(--border-color);
  border-radius: 20px;
  box-shadow: 0 8px 32px rgba(31, 38, 135, 0.37);
  backdrop-filter: blur(20px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  z-index: 1001;
}

.chat-window.mobile {
  width: 100vw;
  height: 100vh;
  right: 0;
  bottom: 0;
  border-radius: 0;
}

.chat-header {
  padding: 15px 20px;
  background: var(--header-bg);
  border-bottom: 2px solid var(--border-color);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-title {
  display: flex;
  flex-direction: column;
  /* align-items: center; */
}

.chat-header-title {
  max-width: 180px;
  margin: 0;
  color: var(--text-color);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.agent-tag-button {
  max-width: 170px;
  background: transparent;
  border: none;
  color: var(--accent-color);
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.agent-tag-button:hover {
  text-decoration: underline;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
  position: relative;
}

.header-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 2px solid var(--accent-color);
}

.header-controls {
  display: flex;
  gap: 10px;
}

.theme-toggle, .close-button {
  background: var(--button-bg);
  border: 1px solid var(--border-color);
  color: var(--text-color);
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
}

.theme-toggle:hover, .close-button:hover {
  background: var(--button-hover-bg);
  transform: scale(1.05);
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.message {
  max-width: 85%;
  margin-bottom: 8px;
}

.agent-message {
  align-self: flex-start;
}

.user-message {
  align-self: flex-end;
}

.message-content {
  color: var(--text-color);
  background: var(--message-bg);
  padding: 12px 16px;
  border-radius: 16px;
  border: 1px solid var(--border-color);
  position: relative;
  overflow: hidden;
}

.user-message .message-content {
  background: var(--user-message-bg);
}

::v-deep(.message-content .button-link) {
  color: var(--accent-color);
  text-decoration: none;
  transition: all 0.3s ease;
  background: transparent;
  border: none;
  cursor: pointer;
  font-weight: bold;
  font-size: 1em;
  margin: 0;
  padding: 0;
}
::v-deep(.message-content .button-link:hover) {
  text-decoration: underline;
}

.typing-animation p {
  margin: 8px 0;
  color: var(--text-color);
  font-size: 0.95em;
  line-height: 1.4;
  opacity: 0;
  animation: fadeIn 0.5s forwards;
}

.typing-animation p:nth-child(1) { animation-delay: 0.5s; }
.typing-animation p:nth-child(2) { animation-delay: 1.5s; }
.typing-animation p:nth-child(3) { animation-delay: 2.5s; }

.tech-decoration {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 2px;
}

.tech-line {
  height: 2px;
  background: linear-gradient(90deg, var(--accent-color) 0%, transparent 100%);
  width: 100%;
  animation: techLine 2s 2 both;
}

.tech-dot {
  position: absolute;
  width: 4px;
  height: 4px;
  background: var(--accent-color);
  border-radius: 50%;
  bottom: -1px;
  left: 0;
  animation: techDot 2s 2 both;
}

.chat-input {
  padding: 16px;
  display: flex;
  gap: 12px;
  border-top: 2px solid var(--border-color);
  background: var(--input-bg);
}

.input-wrapper {
  flex: 1;
  position: relative;
  display: flex;
  align-items: center;
}

.input-icon {
  position: absolute;
  left: 12px;
  color: var(--text-muted);
}

.chat-input input {
  flex: 1;
  padding: 12px 12px 12px 12px;
  border: 1px solid var(--border-color);
  border-radius: 12px;
  background: var(--input-field-bg);
  color: var(--text-color);
  font-size: 0.95em;
  transition: all 0.3s ease;
}

.chat-input input:focus {
  outline: none;
  border-color: var(--accent-color);
  /* box-shadow: 0 0 0 2px var(--accent-shadow); */
}

.send-button {
  padding: 12px 24px;
  background: var(--accent-color);
  border: none;
  border-radius: 12px;
  color: var(--button-text);
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
}

.send-button:hover {
  background: var(--accent-hover);
  transform: translateY(-1px);
}

.send-button small {
  opacity: 0.7;
}

/* Animations */
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

@keyframes techLine {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
}

@keyframes techDot {
  0% { left: 0; }
  100% { left: 100%; }
}

@keyframes pulse {
  0% { box-shadow: 0 0 0 0 rgba(var(--accent-color-rgb), 0.4); }
  70% { box-shadow: 0 0 0 10px rgba(var(--accent-color-rgb), 0); }
  100% { box-shadow: 0 0 0 0 rgba(var(--accent-color-rgb), 0); }
}

.chat-window-enter-active,
.chat-window-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.chat-window-enter-from,
.chat-window-leave-to {
  opacity: 0;
  transform: translateY(20px) scale(0.95);
}

/* First Visit Tooltip Styles */
.first-visit-tooltip {
  position: absolute;
  right: calc(100% + 15px);
  top: 50%;
  transform: translateY(-50%);
  background: var(--window-bg);
  border: 2px solid var(--accent-color);
  padding: 12px 35px 12px 15px;
  border-radius: 12px;
  width: max-content;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  z-index: 1000;
}

.first-visit-tooltip::after {
  content: '';
  position: absolute;
  right: -10px;
  top: 50%;
  transform: translateY(-50%);
  border-left: 10px solid var(--accent-color);
  border-top: 8px solid transparent;
  border-bottom: 8px solid transparent;
}

.first-visit-tooltip p {
  margin: 0;
  color: var(--text-color);
  font-size: 0.95em;
  white-space: nowrap;
}

.tooltip-close {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: var(--text-color);
  font-size: 18px;
  cursor: pointer;
  padding: 0;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.7;
  transition: opacity 0.3s ease;
}

.tooltip-close:hover {
  opacity: 1;
}

.tooltip-enter-active,
.tooltip-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.tooltip-enter-from,
.tooltip-leave-to {
  opacity: 0;
  transform: translateY(-50%) translateX(20px);
}

/* Agent Tooltip Styles */
.agent-tooltip {
  width: 100%;
  min-width: 310px;
  padding-right: 40px;
  position: absolute;
  top: 130%;
  left: -5%;
  background: var(--window-bg);
  border: 1px solid var(--border-color);
  padding: 12px;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  white-space: nowrap;
  z-index: 1000;
  display: flex;
  align-items: center;
  gap: 8px;
  border: 2px solid var(--accent-color);
}

.agent-tooltip::before {
  content: '';
  position: absolute;
  top: -10px;
  left: 6%;
  /* transform: translateX(-50%) rotate(45deg); */
  /* width: 12px;
  height: 12px;
  background: var(--accent-color);
  border-left: 1px solid var(--accent-color);
  border-top: 1px solid var(--accent-color);
   */
  border-left: 8px solid transparent;
  border-bottom: 10px solid var(--accent-color);
  border-right: 8px solid transparent;
}

.agent-tooltip p {
  margin: 0;
  font-size: 0.9rem;
  color: var(--text-color);
}

.agent-tooltip .tooltip-close {
  background: none;
  border: none;
  color: var(--text-color);
  font-size: 1.2rem;
  padding: 0;
  cursor: pointer;
  opacity: 0.7;
  transition: opacity 0.2s;
}

.agent-tooltip .tooltip-close:hover {
  opacity: 1;
}

.agent-tooltip-enter-active,
.agent-tooltip-leave-active {
  transition: all 0.3s ease;
}

.agent-tooltip-enter-from,
.agent-tooltip-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(-10px);
}

/* Theme Variables */
.ai-agent-container {
  --window-bg: #1a1b2e;
  --header-bg: #252642;
  --input-bg: #252642;
  --input-field-bg: #1a1b2e;
  --message-bg: #2a2c4c;
  --user-message-bg: #2A2C64;
  --border-color: #3f4161;
  --text-color: #ffffff;
  --text-muted: #9195b0;
  --accent-color: rgb(215, 77, 36);
  --accent-hover: rgb(215, 77, 36);
  --accent-shadow: rgba(82, 255, 168, 0.2);
  --button-bg: #2a2c4c;
  --button-hover-bg: #3f4161;
  --button-text: #fff;
}

.ai-agent-container.light-theme {
  --window-bg: #ffffff;
  --header-bg: #f8f9fd;
  --input-bg: #f8f9fd;
  --input-field-bg: #ffffff;
  --message-bg: #f0f2f9;
  --user-message-bg: #F0F2EC;
  --border-color: #e1e5ee;
  --text-color: #1a1b2e;
  --text-muted: #6b7280;
  --accent-color: rgb(215, 77, 36);
  --accent-hover: rgb(215, 77, 36);
  --accent-shadow: rgba(82, 255, 168, 0.2);
  --button-bg: #f0f2f9;
  --button-hover-bg: #e1e5ee;
  --button-text: #fff;
}

.avatar-button {
  display: flex;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  transition: transform 0.2s;
  position: relative;
  transform: scale(1.1);
}

.avatar-button:hover {
  transform: scale(1.15);
}

.pulse-animation {
  animation: pulse3 6s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes pulse3 {
  0% {
    transform: scale(1.1);
    box-shadow: 0 0 0 0 rgba(var(--accent-color-rgb), 0.7);
  }
  25% {
    transform: scale(1.15);
    box-shadow: 0 0 10px 5px rgba(var(--accent-color-rgb), 0.4);
  }
  50% {
    transform: scale(1.1);
    box-shadow: 0 0 20px 10px rgba(var(--accent-color-rgb), 0);
  }
  75% {
    transform: scale(1.13);
    box-shadow: 0 0 15px 7px rgba(var(--accent-color-rgb), 0.3);
  }
  100% {
    transform: scale(1.1);
    box-shadow: 0 0 0 0 rgba(var(--accent-color-rgb), 0.7);
  }
}
</style>
